/**
 * Procedural traffic geometry. Owned by `traffic`.
 *
 * Everything here is built once at init() from flat arrays and handed over as a
 * BufferGeometry per vehicle kind, ready for an InstancedMesh. There are no external assets
 * (N1) and no runtime allocation — this file only runs during init()/reset().
 *
 * THE COLOUR TRICK. `traffic.paint` is a MeshStandardMaterial. Three multiplies
 *     diffuse = material.color × vColor(vertex) × instanceColor
 * so one mesh per vehicle kind can carry the whole car:
 *   · material.color  — the shared paint colour, kept in sync with `look`'s weather grade
 *   · instanceColor   — this car's paint, as a ratio against the palette base
 *   · vertex colour   — which PART of the car this is (window 0.085, bumper 0.42, body 1.0)
 * That is one draw call per kind for body, glass, trim and all. Windows come out as a very
 * dark version of the body colour, which is exactly what the Dreamcast traffic does and is
 * far more readable at speed than real transparent glass.
 *
 * Silhouette is the whole job. Reference §3.7 ranks traffic 4th in contrast: it must read as
 * a chunky dark block with an unmistakable shape (sedan ≠ van ≠ bus) from 60 m away.
 */

import * as THREE from 'three';
import { TUNING } from './tuning.js';

const T = TUNING;

/* ══════════════════════════════════════════════════════════════════════ mesher ══ */

/** Minimal triangle-soup builder with per-vertex colour. init()-only, so arrays are fine. */
export class Mesher {
  constructor() {
    this.pos = [];
    this.nor = [];
    this.uv = [];
    this.col = [];
    this.idx = [];
  }

  get vertexCount() { return this.pos.length / 3; }

  /** One quad, CCW from outside. `c` is [r,g,b]. */
  quad(a, b, c, d, col, uvScale = 0.25) {
    const i0 = this.vertexCount;
    // Newell normal — robust for slightly non-planar quads (tapered car bodies).
    let nx = 0, ny = 0, nz = 0;
    const pts = [a, b, c, d];
    for (let i = 0; i < 4; i++) {
      const p = pts[i], q = pts[(i + 1) % 4];
      nx += (p[1] - q[1]) * (p[2] + q[2]);
      ny += (p[2] - q[2]) * (p[0] + q[0]);
      nz += (p[0] - q[0]) * (p[1] + q[1]);
    }
    const nl = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
    nx /= nl; ny /= nl; nz /= nl;

    // Planar UVs on the dominant axis so the paint flake texture has some scale.
    const ax = Math.abs(nx), ay = Math.abs(ny), az = Math.abs(nz);
    for (let i = 0; i < 4; i++) {
      const p = pts[i];
      this.pos.push(p[0], p[1], p[2]);
      this.nor.push(nx, ny, nz);
      if (ax >= ay && ax >= az) this.uv.push(p[2] * uvScale, p[1] * uvScale);
      else if (ay >= az) this.uv.push(p[0] * uvScale, p[2] * uvScale);
      else this.uv.push(p[0] * uvScale, p[1] * uvScale);
      this.col.push(col[0], col[1], col[2]);
    }
    this.idx.push(i0, i0 + 1, i0 + 2, i0, i0 + 2, i0 + 3);
  }

  /**
   * Axis-aligned box with optional per-face colours and a taper.
   * `o.top` scales the +Y face in X and Z; `o.frontY`/`o.backY` shear the top face along Z.
   */
  box(cx, cy, cz, sx, sy, sz, col, o = {}) {
    const hx = sx * 0.5, hy = sy * 0.5, hz = sz * 0.5;
    const tx = o.topX != null ? o.topX : 1, tz = o.topZ != null ? o.topZ : 1;
    const hxT = hx * tx, hzT = hz * tz;
    const fz = o.frontZ != null ? o.frontZ : 0;   // +Z shift of the top face
    const bz = o.backZ != null ? o.backZ : 0;     // -Z shift of the top face

    const x0 = cx - hx, x1 = cx + hx, y0 = cy - hy, y1 = cy + hy, z0 = cz - hz, z1 = cz + hz;
    const X0 = cx - hxT, X1 = cx + hxT, Z0 = cz - hzT + bz, Z1 = cz + hzT + fz;

    const top = o.top || col, side = o.side || col, front = o.front || col;
    const back = o.back || col, bottom = o.bottom || col;

    // +Y
    this.quad([X0, y1, Z1], [X1, y1, Z1], [X1, y1, Z0], [X0, y1, Z0], top);
    // -Y
    if (o.noBottom !== true) this.quad([x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1], bottom);
    // +Z (front)
    this.quad([x0, y0, z1], [x1, y0, z1], [X1, y1, Z1], [X0, y1, Z1], front);
    // -Z (back)
    this.quad([x1, y0, z0], [x0, y0, z0], [X0, y1, Z0], [X1, y1, Z0], back);
    // +X
    this.quad([x1, y0, z1], [x1, y0, z0], [X1, y1, Z0], [X1, y1, Z1], side);
    // -X
    this.quad([x0, y0, z0], [x0, y0, z1], [X0, y1, Z1], [X0, y1, Z0], side);
  }

  /** A flat quad in the XY plane facing +Z. Used for lamp faces and liveries. */
  plateZ(cx, cy, cz, w, h, col) {
    const hw = w * 0.5, hh = h * 0.5;
    this.quad([cx - hw, cy - hh, cz], [cx + hw, cy - hh, cz], [cx + hw, cy + hh, cz], [cx - hw, cy + hh, cz], col, 1);
  }

  /** A flat quad in the XZ plane facing +Y. Contact shadows and ground decals. */
  plateY(cx, cy, cz, w, d, col) {
    const hw = w * 0.5, hd = d * 0.5;
    this.quad([cx - hw, cy, cz + hd], [cx + hw, cy, cz + hd], [cx + hw, cy, cz - hd], [cx - hw, cy, cz - hd], col, 1);
  }

  /** A flat quad in the YZ plane facing ±X. */
  plateX(cx, cy, cz, d, h, col, sign) {
    const hd = d * 0.5, hh = h * 0.5;
    if (sign > 0) this.quad([cx, cy - hh, cz + hd], [cx, cy - hh, cz - hd], [cx, cy + hh, cz - hd], [cx, cy + hh, cz + hd], col, 1);
    else this.quad([cx, cy - hh, cz - hd], [cx, cy - hh, cz + hd], [cx, cy + hh, cz + hd], [cx, cy + hh, cz - hd], col, 1);
  }

  /** Cylinder along the X axis (a wheel). */
  wheelX(cx, cy, cz, r, w, segs, tread, rim) {
    const hw = w * 0.5;
    for (let i = 0; i < segs; i++) {
      const a0 = (i / segs) * Math.PI * 2, a1 = ((i + 1) / segs) * Math.PI * 2;
      const c0 = Math.cos(a0) * r, s0 = Math.sin(a0) * r;
      const c1 = Math.cos(a1) * r, s1 = Math.sin(a1) * r;
      // tread
      this.quad(
        [cx - hw, cy + s0, cz + c0], [cx + hw, cy + s0, cz + c0],
        [cx + hw, cy + s1, cz + c1], [cx - hw, cy + s1, cz + c1], tread, 1
      );
      // Both discs, as triangles fanned from the centre.
      //
      // WINDING. Looking down −X at the +X face, screen-right is −Z and screen-up is +Y, so
      // walking the rim in order of INCREASING angle traces clockwise on screen and the fan
      // comes out back-facing. Both discs were wound that way and had therefore never drawn
      // a single pixel since round 1: every traffic wheel in the game was a hollow tread ring
      // with a bright bar across it, which is the pale blob under every vehicle in
      // `winter-slush` and most of why round 4 read traffic as having no wheels.
      this._tri([cx + hw, cy, cz], [cx + hw, cy + s1, cz + c1], [cx + hw, cy + s0, cz + c0], rim, 1, 0, 0);
      this._tri([cx - hw, cy, cz], [cx - hw, cy + s0, cz + c0], [cx - hw, cy + s1, cz + c1], rim, -1, 0, 0);
    }
  }

  /**
   * A PARKED wheel: one cylinder along X with only the OUTBOARD disc.
   *
   * The inboard disc is never seen — a parked car is only ever read from outside — and the
   * upper half of the tyre is hidden behind the flank because the wheel is seated 5 mm
   * INBOARD of the bodywork, which is what puts it in an arch instead of on top of the
   * paint. That is the whole trick: `segs` faces of tread plus `segs` disc triangles, and
   * the body itself does the arch cut for free with its own depth.
   *
   * `side` is +1 for the right flank (outboard face at +X) and −1 for the left.
   */
  wheelParked(cx, cy, cz, r, w, segs, tread, rim, side) {
    const xIn = cx - side * w, xOut = cx;
    for (let i = 0; i < segs; i++) {
      const a0 = (i / segs) * Math.PI * 2, a1 = ((i + 1) / segs) * Math.PI * 2;
      const c0 = Math.cos(a0) * r, s0 = Math.sin(a0) * r;
      const c1 = Math.cos(a1) * r, s1 = Math.sin(a1) * r;
      if (side > 0) {
        this.quad([xIn, cy + s0, cz + c0], [xOut, cy + s0, cz + c0],
          [xOut, cy + s1, cz + c1], [xIn, cy + s1, cz + c1], tread, 1);
        this._tri([xOut, cy, cz], [xOut, cy + s1, cz + c1], [xOut, cy + s0, cz + c0], rim, 1, 0, 0);
      } else {
        this.quad([xOut, cy + s0, cz + c0], [xIn, cy + s0, cz + c0],
          [xIn, cy + s1, cz + c1], [xOut, cy + s1, cz + c1], tread, 1);
        this._tri([xOut, cy, cz], [xOut, cy + s0, cz + c0], [xOut, cy + s1, cz + c1], rim, -1, 0, 0);
      }
    }
  }

  /**
   * The hard contact blob, as a chamfered rectangle in the XZ plane. Six triangles.
   * A plain rectangle reads as a black sticker; cutting the corners is the cheapest thing
   * that makes it read as a shadow instead.
   */
  contactBlob(cx, cy, cz, w, l, col) {
    const hw = w * 0.5, hl = l * 0.5;
    const a = hw * 0.60, b = hl * 0.66;
    const p = [
      [cx - a, cy, cz + hl], [cx + a, cy, cz + hl], [cx + hw, cy, cz + b], [cx + hw, cy, cz - b],
      [cx + a, cy, cz - hl], [cx - a, cy, cz - hl], [cx - hw, cy, cz - b], [cx - hw, cy, cz + b],
    ];
    for (let i = 1; i < p.length - 1; i++) this._tri(p[0], p[i], p[i + 1], col, 0, 1, 0);
  }

  _tri(a, b, c, col, nx, ny, nz) {
    const i0 = this.vertexCount;
    const pts = [a, b, c];
    for (let i = 0; i < 3; i++) {
      const p = pts[i];
      this.pos.push(p[0], p[1], p[2]);
      this.nor.push(nx, ny, nz);
      this.uv.push(p[0] * 0.5 + 0.5, p[1] * 0.5 + 0.5);
      this.col.push(col[0], col[1], col[2]);
    }
    this.idx.push(i0, i0 + 1, i0 + 2);
  }

  toGeometry(name) {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(this.pos), 3));
    g.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(this.nor), 3));
    g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(this.uv), 2));
    g.setAttribute('color', new THREE.BufferAttribute(new Float32Array(this.col), 3));
    g.setIndex(this.idx);
    g.computeBoundingSphere();
    g.name = name;
    return g;
  }
}

/* ═══════════════════════════════════════════════════════════════════ helpers ══ */

const V = T.visual.tone;
const g = (k) => [k, k, k];

/** sRGB hex → linear rgb triple, matching what three does to a material colour. */
const _tmpCol = new THREE.Color();
export function hexToLinear(hex, out) {
  _tmpCol.setHex(hex, THREE.SRGBColorSpace);
  if (out) { out[0] = _tmpCol.r; out[1] = _tmpCol.g; out[2] = _tmpCol.b; return out; }
  return [_tmpCol.r, _tmpCol.g, _tmpCol.b];
}

/* ═════════════════════════════════════════════════════════════════ vehicles ══ */

/**
 * Build one vehicle body. Local frame: +Z forward, +Y up, origin on the ground between the
 * axles, so the instance matrix is just (position, yaw).
 *
 * The single most important number here is `floor`: the underside of the bodywork. In the
 * first pass a full-width sill box ran from the ground to 0.34 m and swallowed the wheels
 * whole, so every car read as a slab parked on the tarmac. The shell now starts ABOVE the
 * wheel centres and the wheels sit flush with the flanks, which is what gives these things a
 * planted stance at 60 m.
 */
function buildVehicle(kind, m) {
  const mb = new Mesher();
  const hw = m.W * 0.5, hl = m.L * 0.5;

  const body = g(V.body);
  const roofC = g(V.roof);
  const win = g(V.window);
  const bump = g(V.bumper);
  const dark = g(V.dark);
  const chrome = g(V.chrome);

  const boxy = !!m.boxy;
  const cabZ0 = m.cabZ0 * m.L;
  const cabZ1 = m.cabZ1 * m.L;
  const cabC = (cabZ0 + cabZ1) * 0.5;
  const cabLen = Math.max(0.7, cabZ1 - cabZ0);
  const cabH = m.roof - m.beltline;
  const lowH = m.beltline - m.floor;

  // ── under-skirt: a dark slab between the axles. It MUST be narrower than the wheels'
  // inner faces and shorter than the wheelbase, or it eats the wheels in side view.
  mb.box(0, m.floor - 0.06, 0, m.W * 0.60, 0.24, m.L * 0.50, dark);

  // ── lower body, floor → beltline. Tapered nose and tail carry the silhouette.
  mb.box(0, m.floor + lowH * 0.5, 0, m.W, lowH, m.L, body, {
    top: body, topX: 0.99, topZ: 1.0,
    frontZ: -m.noseTaper, backZ: m.tailTaper,
    bottom: dark,
  });

  // ── flank crease + sill band. See TUNING.visual.tone.crease. These are the only thing
  //    stopping a van from being one flat rectangle of colour five metres long, and they cost
  //    four triangles each. Placed 4 mm proud of the untapered lower flank, so no z-fight.
  {
    const crease = g(V.body * V.crease);
    const sillC = g(V.body * V.sill);
    const creaseY = m.floor + lowH * 0.66;
    const sillY = m.floor + T.visual.sillH * 0.5;
    for (let i = 0; i < 2; i++) {
      const s = i === 0 ? 1 : -1;
      mb.plateX(s * (hw + 0.004), creaseY, 0, m.L * 0.86, T.visual.creaseH, crease, s);
      mb.plateX(s * (hw + 0.004), sillY, 0, m.L * 0.93, T.visual.sillH, sillC, s);
    }
  }

  // ── greenhouse
  //
  // A boxy vehicle's glasshouse IS its whole flank, so painting it the window tone turned an
  // STM bus into one untextured navy slab six metres long — round 4's "untextured
  // flat-shaded navy box". Vans and buses therefore get PAINTED sides with a dark window
  // band struck through them below; cars keep the dark-all-round glasshouse, which at their
  // scale reads as glazing rather than as a hole.
  const cabSide = boxy ? g(V.pillar) : win;
  mb.box(0, m.beltline + cabH * 0.5, cabC, m.W * (boxy ? 0.995 : 0.925), cabH, cabLen, cabSide, {
    top: roofC, side: cabSide, front: boxy ? win : win, back: cabSide,
    // Keep the greenhouse chunky. A hard taper turns the roof into a sliver and the car
    // stops reading as a car at 60 m, which is the only distance that matters.
    topX: boxy ? 0.99 : 0.93, topZ: boxy ? 0.99 : 0.87,
    frontZ: boxy ? -0.05 : -cabLen * 0.15,
    backZ: boxy ? 0.04 : cabLen * 0.09,
    noBottom: true,
  });

  if (boxy) {
    // Vans and buses: a DARK window band struck through the painted flank. It used to be
    // g(1.28) — a *brighter* version of the paint, which on a navy bus is still navy and
    // fought the livery stripe for the same read.
    const bandY = m.beltline + cabH * 0.62;
    const bandH = Math.min(0.86, cabH * 0.46);
    for (let i = 0; i < 2; i++) {
      const s = i === 0 ? 1 : -1;
      mb.plateX(s * (m.W * 0.5 + 0.006), bandY, cabC, cabLen * 0.93, bandH, win, s);
    }
    mb.box(0, m.roof + 0.04, cabC, m.W * 0.58, 0.08, cabLen * 0.72, g(V.roof * 0.8), { noBottom: true });
  }

  if (m.bedZ0 != null) {
    // Pickup bed: an open box BEHIND the cab (−Z is the rear).
    const bz0 = m.bedZ0 * m.L, bz1 = m.bedZ1 * m.L;
    const bl = bz1 - bz0, bc = (bz0 + bz1) * 0.5;
    const wallY = m.beltline + m.bedWallH * 0.5;
    for (let i = 0; i < 2; i++) {
      const s = i === 0 ? 1 : -1;
      mb.box(s * (hw - 0.08), wallY, bc, 0.16, m.bedWallH, bl, body, { noBottom: true });
    }
    mb.box(0, wallY, bz0 + 0.08, m.W * 0.94, m.bedWallH, 0.16, body, { noBottom: true });
    mb.box(0, m.beltline + 0.03, bc, m.W * 0.88, 0.05, bl * 0.92, dark, { noBottom: true });
  }

  // ── bumpers
  mb.box(0, m.floor + 0.06, hl - 0.10, m.W * 0.97, 0.28, 0.24, bump, { noBottom: true });
  mb.box(0, m.floor + 0.06, -hl + 0.10, m.W * 0.97, 0.28, 0.24, bump, { noBottom: true });

  // ── grille + plates
  mb.plateZ(0, m.floor + lowH * 0.55, hl + 0.006 - m.noseTaper * 0.30, m.W * 0.46, 0.22, dark);
  mb.plateZ(0, m.floor + 0.02, hl + 0.008, 0.42, 0.14, chrome);
  mb.plateZ(0, m.floor + 0.02, -hl - 0.008, 0.42, 0.14, chrome);

  // ── mirrors
  const mirZ = cabZ1 - 0.14;
  for (let i = 0; i < 2; i++) {
    const s = i === 0 ? 1 : -1;
    mb.box(s * (hw + 0.08), m.beltline + 0.07, mirZ, 0.18, 0.11, 0.07, dark, { noBottom: true });
  }

  // ── taxi: roof sign + chequer stripe at the beltline
  if (m.roofSign) {
    mb.box(0, m.roof + 0.13, 0.30, 0.66, 0.24, 0.22, g(V.light), { noBottom: true });
  }
  if (m.checker) {
    const n = 12;
    const step = (m.L - 1.1) / n;
    const yBand = m.floor + lowH * 0.32;
    for (let i = 0; i < n; i++) {
      const zz = -hl + 0.55 + (i + 0.5) * step;
      const c = i % 2 === 0 ? g(1.30) : g(0.09);
      for (let k = 0; k < 2; k++) {
        const s = k === 0 ? 1 : -1;
        mb.plateX(s * (hw + 0.006), yBand, zz, step, 0.19, c, s);
      }
    }
  }

  if (m.livery) {
    // STM bus: a light band low on the flank plus a pale destination blind up front.
    for (let i = 0; i < 2; i++) {
      const s = i === 0 ? 1 : -1;
      mb.plateX(s * (hw + 0.007), m.floor + 0.16, 0, m.L * 0.96, 0.26, g(1.28), s);
    }
    mb.plateZ(0, m.beltline + cabH * 0.84, hl + 0.010 - 0.02, m.W * 0.52, 0.22, g(V.light));
  }

  const geo = mb.toGeometry('traffic.body.' + kind);

  // Wheels sit flush with the flanks so they read as wheels, not as bumps under the car.
  const wx = hw - m.wheelW * 0.5 - 0.02;
  const rig = {
    kind,
    halfW: hw,
    halfL: hl,
    height: m.H,
    wheelR: m.wheelR,
    wheelW: m.wheelW,
    wheels: [
      [-wx, m.wheelR, hl * m.axleF * 2],
      [wx, m.wheelR, hl * m.axleF * 2],
      [-wx, m.wheelR, hl * m.axleR * 2],
      [wx, m.wheelR, hl * m.axleR * 2],
    ],
    wheelX: wx,
    // Long vehicles get a second rear axle so the wheelbase does not look absurd.
    extraAxle: m.L > 6 ? hl * (m.axleR * 2 + 0.26) : 0,
    headZ: hl + 0.02 - m.noseTaper * 0.30,
    headY: m.headY,
    headX: hw * m.headX,
    tailZ: -hl - 0.02,
    tailY: m.tailY,
    tailX: hw * m.tailX,
    indZ: hl + 0.01 - m.noseTaper * 0.30,
    indY: m.headY - 0.02,
    indX: hw * Math.min(0.97, m.headX + 0.26),
    indRearZ: -hl - 0.03,
    indRearY: m.tailY,
    indRearX: hw * Math.min(0.97, m.tailX + 0.22),
  };

  return { geo, rig };
}

/* ══════════════════════════════════════════════════════════════════ cyclist ══ */

/**
 * The cyclist is TWO meshes, and the reason matters.
 *
 * Round 1 put the whole cyclist — bike, wheels, rider, head — into one instanced mesh on the
 * cloth material with the jacket pushed through instanceColor. instanceColor multiplies
 * EVERYTHING, so the bicycle came out bright blue and the rider's face came out bright blue,
 * and the whole thing read as a smurf on a smurf. Splitting the frame onto the (untinted)
 * tyre material fixes the bike, and the rider now wears a colour-matched helmet instead of
 * having a tinted face.
 *
 * Wheels are NOT built into either mesh — they come from the shared wheel InstancedMesh, so
 * they get the right rubber material and they actually rotate.
 */
function buildCyclistRider() {
  const b = T.bike;
  const mb = new Mesher();
  const jacket = g(1.0);                          // ×instanceColor = the jacket colour
  const sleeve = g(T.visual.riderSleeve);                // one value down, so the arms read as arms
  const trouser = g(T.visual.riderTrouser);

  // ── legs, BENT. A rider whose legs run straight to the tarmac is a person standing over a
  //    bike; the pose is most of what says "riding" at 60 px. Thigh slopes hip → knee, shin
  //    knee → pedal, using the box shear rather than a rotation (the Mesher has no transform
  //    stack and this costs the same 12 triangles either way).
  for (let i = 0; i < 2; i++) {
    const s = i === 0 ? 1 : -1;
    // thigh: bottom at the knee (forward, low), top at the hip (back, high)
    mb.box(s * 0.105, 0.72, 0.09, 0.155, 0.34, 0.20, trouser, { noBottom: true, frontZ: -0.20, backZ: -0.20 });
    // shin: bottom at the pedal, top at the knee
    mb.box(s * 0.105, 0.38, 0.14, 0.130, 0.36, 0.17, trouser, { noBottom: true, frontZ: -0.06, backZ: -0.06 });
    mb.box(s * 0.105, 0.20, 0.20, 0.135, 0.07, 0.22, g(T.visual.riderTrouser * 0.6), { noBottom: true });  // shoe
  }

  // ── torso, LEANED FORWARD over the bars. The top face is sheared +Z, so the shoulders sit
  //    ahead of the hips at about 28° — the silhouette a cyclist actually has.
  //
  //    ⚠ THE SHEAR WAS 0.17 AND IT PRODUCED A "PAPER SAIL". Attribution: the `combo-hud`
  //    figure four critics described as an untextured mannequin is a CYCLIST, not a pedestrian
  //    — proven by hiding one traffic node at a time over that region (§4.9b,
  //    `tools/tr-probe.mjs --hide-each`), which named `traffic.cyclists.rider` +
  //    `.bike` + `.wheels` + `.blobshadow` and nothing else. At 0.17 the sheared top face was
  //    a 0.42 x 0.30 m unbroken plane tilted straight at the key light, and from the elevated
  //    chase camera it read as a triangular white sail floating beside the rider. 0.105 plus
  //    the yoke band below breaks it into two values.
  mb.box(0, 1.06, 0.03, b.torsoW, 0.50, b.torsoT, jacket, {
    noBottom: true, topZ: 0.90, topX: 0.94, frontZ: 0.105, backZ: 0.105,
  });
  // Shoulder yoke: one darker band across the top of the back. This is the value break that
  // stops the shoulder plane reading as a single lit sheet from above.
  mb.box(0, 1.28, 0.115, b.torsoW * 0.98, 0.075, b.torsoT * 0.94, sleeve,
    { noBottom: true, topX: 0.88, topZ: 0.86 });

  // ── arms, sloping DOWN AND FORWARD from the shoulder to the handlebar.
  for (let i = 0; i < 2; i++) {
    const s = i === 0 ? 1 : -1;
    mb.box(s * (b.torsoW * 0.5 + 0.035), 1.10, 0.38, 0.10, 0.40, 0.13, sleeve,
      { noBottom: true, frontZ: -0.20, backZ: -0.20 });
  }

  // NO HEAD HERE. The helmet, face and hands live on the untinted bike mesh — see
  // `TUNING.visual.riderTone` for why, and for the round-8 measurement that forced it.
  return mb.toGeometry('traffic.cyclist.rider');
}

function buildCyclistBike() {
  const b = T.bike;
  const mb = new Mesher();
  // Same trap as the wheels: this mesh is on `traffic.tyre`, so these are ABSOLUTE albedos.
  const B = T.visual.bikeTone;
  const R = T.visual.riderTone;
  const dark = g(B.dark);
  const frame = g(B.frame);
  const bright = g(B.bright);
  const hb = b.base * 0.5;

  mb.box(0, b.frameH * 0.62, 0, 0.07, 0.08, b.base * 0.90, frame, { noBottom: true });
  mb.box(0, b.frameH + 0.18, -0.10, 0.06, 0.34, 0.06, frame, { noBottom: true });
  mb.box(0, b.barH * 0.62, hb - 0.06, 0.06, b.barH * 0.92, 0.06, frame, { noBottom: true });
  mb.box(0, b.barH, hb - 0.07, 0.50, 0.06, 0.06, frame, { noBottom: true });   // handlebar
  mb.box(0, b.saddleH, -0.15, 0.14, 0.06, 0.28, dark, { noBottom: true });     // saddle
  mb.box(0, 0.30, -hb + 0.06, 0.09, 0.09, 0.30, dark, { noBottom: true });     // chainstay
  mb.plateZ(0, 0.72, -hb - 0.05, 0.10, 0.06, bright);                          // rear reflector

  // ── THE RIDER'S HEAD, on this mesh so it is never tinted by the jacket colour.
  //    Sits ahead of the hips to match the leaned torso built in buildCyclistRider().
  const hy = 1.41, hz = 0.23;
  mb.box(0, hy, hz, 0.185, 0.20, 0.185, R.skin, { noBottom: true });                    // face/neck
  mb.box(0, hy + 0.15, hz - 0.005, 0.235, 0.13, 0.235, R.helmet,                        // helmet shell
    { noBottom: true, topX: 0.80, topZ: 0.80 });
  // Crown block. A helmet that is one tapered slab reads as a flat cap from every angle
  // except dead-on; a second, smaller volume on top is what gives it a dome silhouette.
  mb.box(0, hy + 0.222, hz - 0.010, 0.176, 0.052, 0.176, R.helmet,
    { noBottom: true, topX: 0.66, topZ: 0.68 });
  mb.plateZ(0, hy + 0.155, hz + 0.121, 0.20, 0.045, R.helmetHi);                        // helmet flash
  mb.plateZ(0, hy + 0.02, hz + 0.095, 0.15, 0.06, R.visor);                             // shades
  // Rear vent stripe and chin straps. The rider is seen from BEHIND for most of the game, and
  // from there the old head was one unbroken dark box on one unbroken skin box.
  mb.plateZ(0, hy + 0.155, hz - 0.126, 0.17, 0.040, R.helmetHi);
  for (let i = 0; i < 2; i++) {
    const s = i === 0 ? 1 : -1;
    mb.plateX(s * 0.094, hy + 0.030, hz + 0.010, 0.085, 0.130, R.helmet, s);
  }
  for (let i = 0; i < 2; i++) {
    const s = i === 0 ? 1 : -1;
    mb.box(s * 0.20, b.barH + 0.03, hb - 0.07, 0.085, 0.085, 0.10, R.skin, { noBottom: true }); // hands
  }

  return mb.toGeometry('traffic.cyclist.bike');
}

/* ═════════════════════════════════════════════════════════════ pedestrians ══ */

/**
 * THREE TINT CHANNELS, THREE MESHES. Read this before touching any of the three builders.
 *
 * WHAT WAS WRONG (round 10, `combo-hud`, major — "blocky mannequin figures"; and the brief's
 * one-line version, "pedestrians are untextured mannequins"):
 *
 *   Everything below the neck lived on ONE instanced mesh whose diffuse is multiplied by the
 *   coat colour. Trousers were `coat × 0.26` and sleeves `coat × 0.62`, so a pedestrian was a
 *   single hue from collar to shoe — a red coat came with red trousers and red shoes. That is
 *   the literal definition of "one value and a capsule": the figure had three VALUES but only
 *   ever one HUE, and hue is what separates a person from a painted dowel at 40 px.
 *
 *   Worse at close range. The `combo-hud` capture puts a pedestrian 7.2 m from the camera
 *   filling 236 × 129 px, and it resolves into pale slabs with two orange blocks — it is the
 *   "unexplained concrete lump exiting the bottom of frame" in the round-10 `vehicle` fatal.
 *   That defect is ours, not the truck's; verified by toggling `traffic.*` invisible and
 *   re-rendering, which removes the lump and nothing else in that region (§4.9b).
 *
 * THE SPLIT
 *   `pedBody[k]`  — char.cloth, instanceColor = COAT.     Torso, arms, headwear, bag/pack.
 *   `pedLegs`     — char.cloth, instanceColor = TROUSERS. Hips, thighs, shins, shoes.
 *   `pedHead`     — char.skin,  instanceColor = SKIN.     Neck, skull, hair, hands.
 *
 * The legs mesh is the whole point: one extra draw call buys an INDEPENDENT second garment
 * colour on every pedestrian in the city. `pedBody` is an ARRAY because a tint cannot change
 * an outline, and the critic asked for silhouette variation specifically.
 *
 * WHY THE JOINTS. The old figure was straight-limbed boxes. A knee and an elbow are the two
 * breaks the eye uses to resolve a human outline, and they cost 20 triangles each on geometry
 * that is built once. A pedestrian is now ~230 triangles against a 900k budget; the whole
 * 48-strong crowd is under 12k.
 */

/**
 * LEGS — their own mesh, their own tint. Mid-stride and asymmetric: the +1 side steps
 * FORWARD, the −1 side trails, and each leg breaks at the knee. A figure reads as WALKING in
 * a still frame only if the near leg is forward and the near arm is back; that contrapposto
 * is the entire cue and it is free, because the geometry is built once and every instance
 * inherits it.
 *
 * Baking one stride means every pedestrian is at the same point in the cycle. That is the
 * right trade at this scale — they carry different headings, coats, builds and bob phases, so
 * the repeat is invisible, and a second stride geometry would cost a whole draw call to fix
 * something nobody can see.
 */
function buildPedLegs(pose) {
  const p = T.pedModel;
  const B = T.pedBuild;
  const mb = new Mesher();
  const cloth = g(1.0);
  const shin = g(B.shinTone);
  const cuff = g(B.cuffTone);

  const legX = B.legX * pose.spread;

  // Hips: closes the crotch AND fills the body between the top of the thighs and the highest
  // garment hem, so no archetype can leave a see-through slot at the waist (see tuning).
  mb.box(0, (B.hipY0 + B.hipY1) * 0.5, 0, B.hipW, B.hipY1 - B.hipY0, B.hipD, cloth,
    { noBottom: true, topX: 0.94, topZ: 0.94 });

  for (const s of [1, -1]) {
    const swing = s > 0 ? pose.swing : -pose.swing;
    const x = s * legX;
    // The TRAILING leg (the one swung back) lifts its heel. A stride where both soles are
    // flat on the ground is a mannequin on a turntable, and the heel is 3 px of the only
    // thing in the pose that says the figure is mid-step rather than posed.
    const trailing = swing < 0;
    const lift = trailing ? pose.lift : 0;

    // Thigh: hip → knee. Top face sheared BACK, so the knee ends up ahead of the hip.
    const thighH = p.legH - B.kneeY;
    mb.box(x, (p.legH + B.kneeY) * 0.5, swing * 0.26, B.thighW, thighH + 0.02, B.thighD, cloth,
      { noBottom: true, frontZ: -swing * 0.52, backZ: -swing * 0.52 });

    // Shin: knee → ankle, nearly upright, with the foot slightly further forward again.
    const ankleY = B.ankleY + lift;
    const shinH = B.kneeY - ankleY;
    mb.box(x, (B.kneeY + ankleY) * 0.5, swing * 0.62, B.shinW, shinH + 0.02, B.shinD, shin,
      { noBottom: true, frontZ: -swing * 0.20, backZ: -swing * 0.20 });

    // Cuff: one value down at the ankle. It is what stops the shoe reading as part of the leg.
    mb.box(x, ankleY + B.cuffH * 0.5, swing * 0.72, B.shinW * 1.06, B.cuffH, B.shinD * 1.06,
      cuff, { noBottom: true });

    _pedShoe(mb, x, lift, swing * 0.78 + 0.030, trailing);
  }
  return mb.toGeometry('traffic.ped.legs.' + pose.id);
}

/**
 * ONE SHOE. See `TUNING.pedBuild.shoeH` for why this is four boxes instead of one.
 *
 * Local frame: `x` is the leg centre, `z` the ankle position, `lift` raises the whole shoe
 * (a trailing foot is up on its toe). Every tone is a multiplier on the TROUSER colour, which
 * is the mesh's instanceColor — so the sole is the darkest value on the figure regardless of
 * whether the trousers are stone or denim, and the internal sole/upper step survives it.
 */
function _pedShoe(mb, x, lift, z, trailing) {
  const B = T.pedBuild;
  const sole = g(B.soleTone);
  const upper = g(B.shoeTone);
  const toe = g(B.toeTone);

  // A trailing foot pivots on the ball of the foot: the heel lifts, the toe stays down.
  const heelY = lift;
  const toeY = trailing ? 0 : lift;

  // ── sole: wider and longer than the upper, and only 24 mm tall. This band IS the shoe.
  mb.box(x, heelY + B.soleH * 0.5, z + 0.012, B.shoeW * B.soleW, B.soleH, B.shoeL * B.soleL,
    sole, { noBottom: true, frontZ: toeY - heelY, backZ: 0 });

  // ── upper: heel → instep, full width, top sheared down toward the toe.
  const upH = B.shoeH - B.soleH;
  mb.box(x, heelY + B.soleH + upH * 0.5, z, B.shoeW, upH, B.shoeL - B.toeL, upper,
    { noBottom: true, topZ: 0.90, frontZ: -B.toeDrop, backZ: 0 });

  // ── toe cap: forward of the instep, narrower and lower. This is the wedge that stops the
  //    foot reading as a rectangle from the elevated cameras every one of our shots uses.
  const toeZ = z + (B.shoeL - B.toeL) * 0.5 + B.toeL * 0.5 - 0.004;
  mb.box(x, toeY + B.soleH + (upH - B.toeDrop) * 0.5, toeZ,
    B.shoeW * B.toeNarrow, upH - B.toeDrop, B.toeL, toe,
    { noBottom: true, topX: 0.82, topZ: 0.86, frontZ: -0.010 });

  // ── heel kick: a small block behind the ankle. Without it the shoe is centred under the
  //    shin and the figure reads as standing on two pegs.
  mb.box(x, heelY + B.heelH * 0.5, z - (B.shoeL - B.toeL) * 0.5 - B.heelL * 0.4,
    B.shoeW * 0.86, B.heelH, B.heelL, sole, { noBottom: true });
}

/**
 * ONE BODY ARCHETYPE — torso, arms, headwear and whatever the archetype carries. Tinted per
 * instance by the coat colour, so every vertex colour here is a MULTIPLIER, not an albedo.
 *
 * The silhouette work is done by four things and nothing else matters at this distance:
 *   · `hemY`      — waist-length jacket vs. overcoat below the knee. The biggest single cue.
 *   · `shoulderW` — a parka is visibly broader across the top than a jacket.
 *   · `hat`       — bare head / tuque / raised hood, each a different outline above the neck.
 *   · `pack`/`tote` — an asymmetric lump that breaks the vertical.
 */
/**
 * WHERE ONE ARM IS, FOR A GIVEN ARCHETYPE AND SIDE. The single source of truth for the arm
 * pose, called by BOTH `buildPedBody()` (sleeves) and `buildPedHands()` (hands).
 *
 * That sharing is the point. In round 11 the sleeve was built from an accumulated subtraction
 * in one function and the hand from a different accumulated subtraction in another, and the
 * two disagreed by all but 8 mm — so the hands were drawn inside the sleeves and no shot ever
 * showed one. Anything that moves an arm now moves the hand with it by construction.
 *
 * `s` is +1 for the right arm, −1 for the left. Returns metres in the model frame.
 */
function pedArmGeometry(a, s) {
  const p = T.pedModel;
  const B = T.pedBuild;
  const S = T.pedStride;
  const pose = T.pedArmPoses[a.arms] || T.pedArmPoses.swing;

  const armX = p.shoulder * 0.5 + S.armGap + B.upperArmW * 0.5;
  const shoulderY = p.H * B.shoulderF;
  const armTop = shoulderY - B.shoulderCapH * 0.5;
  const elbowY = armTop - B.upperArmH;

  // A mirrored pose swings the two arms in opposite directions (walking); an unmirrored one
  // puts both arms in the same place (hands in pockets, forearms carried forward).
  const dir = pose.mirror ? (s > 0 ? -1 : 1) : 1;
  const elbowZ = pose.elbow * dir;
  const wristZ = pose.hand * dir;

  // Clamp: the forearm is a sheared box, so the wrist must stay below the elbow by enough to
  // leave a limb. See the warning in TUNING.pedArmPoses.
  const wristY = Math.min(B.wristY + pose.drop, elbowY - 0.075);
  const foreLen = elbowY - wristY;
  const foreX = armX - pose.tuck;

  return {
    armX, armTop, elbowY, elbowZ, wristY, wristZ, foreX, foreLen,
    // upper arm: bottom face at the elbow, top face sheared back to the shoulder at z = 0.
    upperCY: armTop - B.upperArmH * 0.5,
    upperCZ: elbowZ,
    upperShear: -elbowZ,
    // forearm: bottom face at the wrist, top face sheared to the elbow.
    foreCY: wristY + foreLen * 0.5,
    foreCZ: wristZ,
    foreShear: elbowZ - wristZ,
  };
}

function buildPedBody(a) {
  const p = T.pedModel;
  const S = T.pedStride;
  const B = T.pedBuild;
  const mb = new Mesher();

  const coat = g(1.0);
  const sleeve = g(S.sleeveTone);
  const foreSleeve = g(S.sleeveTone * 0.84);
  const cap = g(B.shoulderCapTone);
  const collar = g(B.collarTone);

  const shoulderY = p.H * B.shoulderF;
  const halfW = p.shoulder * 0.5;                 // constant across archetypes — see tuning
  const hemW = p.shoulder * a.hemW;
  const waistW = p.shoulder * 0.94;
  const depth = B.torsoD * a.bulk;

  // ── skirt of the garment: hem → chest. `topX` under 1 flares the hem, which is what makes
  //    the overcoat read as an overcoat rather than as a longer box.
  const skirtH = a.chestY - a.hemY;
  mb.box(0, (a.hemY + a.chestY) * 0.5, 0, hemW, skirtH, depth * a.hemW, coat, {
    noBottom: true, topX: waistW / hemW, topZ: 0.96, bottom: sleeve,
  });
  // Hem band, a value down: it breaks the garment off the legs.
  mb.box(0, a.hemY + S.hemH * 0.5, 0, hemW * 1.02, S.hemH, depth * a.hemW * 1.02,
    sleeve, { noBottom: true });

  // ── chest: waist → shoulder line.
  mb.box(0, (a.chestY + shoulderY) * 0.5, 0, halfW * 2 * 0.94, shoulderY - a.chestY, depth, coat,
    { noBottom: true, topX: 1 / 0.94, topZ: 1.0 });

  // ── shoulder cap: a wider, slightly darker slab. This is the SHOULDER LINE — without it a
  //    coat and a head are one continuous blob from any elevated camera — and it is also what
  //    JOINS the sleeves to the torso (see `shoulderCapW`).
  mb.box(0, shoulderY - B.shoulderCapH * 0.5, 0, halfW * 2 * B.shoulderCapW, B.shoulderCapH,
    depth * 1.02, cap, { noBottom: true, topX: 0.94, topZ: 0.96 });

  // ── collar around the base of the neck: the darkest value on the coat.
  mb.box(0, shoulderY + B.collarH * 0.34, 0, halfW * 2 * B.collarW, B.collarH, depth * 0.80,
    collar, { noBottom: true, topX: 0.86, topZ: 0.86 });

  if (a.scarf) {
    mb.box(0, shoulderY + B.collarH * 0.34 + B.scarfH * 0.55, 0,
      halfW * 2 * B.scarfW, B.scarfH, depth * 0.86, g(B.scarfTone),
      { noBottom: true, topX: 0.90, topZ: 0.90 });
  }

  // ── arms, with an ELBOW. `armX` is a CONSTANT across archetypes — the hands live on their
  //    own mesh and are placed from the SAME pose numbers, so a body that narrowed under a
  //    fixed arm would tear the sleeves off. See the note in TUNING.pedArchetypes.
  //
  //    The POSE is the archetype's (round 12). All three pose variants share this code; only
  //    the four numbers in `TUNING.pedArmPoses` change, and `pedArmGeometry()` below is the
  //    single place that turns them into positions, so the hands cannot drift out of the
  //    sleeves the way they did in round 11.
  const armX = p.shoulder * 0.5 + S.armGap + B.upperArmW * 0.5;
  const cuffBand = g(B.cuffTone2);
  for (const s of [1, -1]) {
    const a2 = pedArmGeometry(a, s);

    mb.box(s * armX, a2.upperCY, a2.upperCZ, B.upperArmW, B.upperArmH + 0.02,
      B.upperArmD, sleeve, { noBottom: true, frontZ: a2.upperShear, backZ: a2.upperShear });

    mb.box(s * a2.foreX, a2.foreCY, a2.foreCZ, B.foreArmW, a2.foreLen + 0.02, B.foreArmD,
      foreSleeve, { noBottom: true, frontZ: a2.foreShear, backZ: a2.foreShear });

    // Cuff band at the sleeve end. A skin block emerging straight out of a sleeve of the same
    // width reads as a bare forearm; one dark value at the wrist reads as a hand in a sleeve.
    mb.box(s * a2.foreX, a2.wristY + B.sleeveCuffH * 0.42, a2.wristZ,
      B.foreArmW * B.sleeveCuffW, B.sleeveCuffH, B.foreArmD * B.sleeveCuffW,
      cuffBand, { noBottom: true });
  }

  // ── headwear. A raised hood is the single most recognisable winter-city outline there is.
  if (a.hat === 'tuque') {
    mb.box(0, p.H - B.tuqueH * 0.34, 0, p.headR * 2 * B.tuqueW, B.tuqueH, p.headR * 2 * B.tuqueW,
      g(B.tuqueTone), { noBottom: true, topX: 0.82, topZ: 0.82 });
  } else if (a.hat === 'hood') {
    // Pushed BACK so the face protrudes through the front of the shell — a closed box centred
    // on the skull would swallow the head and the figure would lose its face entirely.
    const skullCY = p.H - p.headR * B.skullH * 0.5;
    mb.box(0, skullCY + 0.030, B.hoodZ, p.headR * 2 * B.hoodW, B.hoodH, p.headR * 2 * B.hoodD,
      g(B.hoodTone), { noBottom: true, topX: 0.86, topZ: 0.88, backZ: -0.02 });
  }

  if (a.pack) {
    mb.box(0, B.packY, -depth * 0.5 - B.packD * 0.5 + 0.025, B.packW, B.packH, B.packD,
      g(B.packTone), { noBottom: true, topX: 0.92, topZ: 0.9 });
  }
  if (a.tote) {
    // Hangs off the −1 hand, wherever this archetype's pose actually put it.
    const L = pedArmGeometry(a, -1);
    mb.box(-L.foreX - 0.012, L.wristY - 0.02 - B.toteH * 0.5, L.wristZ, B.toteW, B.toteH,
      B.toteD, g(B.toteTone), { noBottom: true });
  }

  return mb.toGeometry('traffic.ped.body.' + a.id);
}

/**
 * HEAD — neck, skull, hair and hands. Tinted per instance by skin tone, on `char.skin`.
 *
 * The NECK is the point of this mesh. The old head was a 0.2375 m box sitting flush on a
 * 0.46 m shoulder, so at any elevated camera angle the face merged into the coat and the
 * figure had no head/torso break at all. A 0.105 m neck two values down, with the shoulder
 * cap on the body mesh underneath it, is what separates them.
 *
 * ROUND 12 — THERE IS A FACE NOW, AND THE OLD COMMENT HERE WAS WRONG.
 *
 * It used to read "No face. §10 does not ask for one and at the distances we ship a pedestrian
 * is three values and a silhouette." The distance assumption was never checked. Projecting the
 * shot cameras (`tools/tr-probe.mjs`) puts a pedestrian **8.2 m from the `combo-hud` camera at
 * 195 px tall** and another at 271 px in `wide-establish` — head widths of 26 and 36 px. Four
 * blind critics in a row wrote "featureless brown box head" / "box head, no face", and they
 * were right: at 26 px an eye band is four pixels, which is four pixels more than nothing.
 *
 * The hands are NOT here any more. They moved to `buildPedHands()` so they can follow each
 * archetype's arm pose; see `pedArmGeometry()`.
 */
function buildPedHead() {
  const p = T.pedModel;
  const B = T.pedBuild;
  const mb = new Mesher();
  const skin = g(1.0);
  const hair = g(B.hairTone);
  const neck = g(B.neckTone);

  const shoulderY = p.H * B.shoulderF;
  const skullH = p.headR * B.skullH;
  const skullW = p.headR * B.skullW;
  const skullD = p.headR * B.skullD;
  const skullCY = p.H - skullH * 0.5;
  const skullY0 = p.H - skullH;

  // Neck, spanning shoulder line → skull base with a small overlap at each end.
  mb.box(0, (shoulderY + skullY0) * 0.5, -0.004, B.neckW, (skullY0 - shoulderY) + B.neckH,
    B.neckD, neck, { noBottom: true });

  // Skull. Slightly narrowed at the crown so it is not a perfect cube.
  mb.box(0, skullCY, 0, skullW, skullH, skullD, skin,
    { noBottom: true, topX: 0.90, topZ: 0.90 });

  // Hair: a cap over the crown PLUS a plate down the back of the skull. The cap alone was
  // invisible from a chase camera looking slightly DOWN at a figure walking away.
  mb.box(0, p.H - p.headR * B.hairCapH * 0.5, -0.004, skullW * 1.03, p.headR * B.hairCapH,
    skullD * 1.04, hair, { noBottom: true, topX: 0.92, topZ: 0.92 });
  mb.box(0, skullCY - skullH * 0.10, -skullD * 0.5 - B.hairBackD * 0.5 + 0.004,
    skullW * 1.01, skullH * 0.74, B.hairBackD, hair, { noBottom: true });

  _pedFace(mb, skullCY, skullW, skullD);

  return mb.toGeometry('traffic.ped.head');
}

/**
 * THE FACE. Five boxes and three quads on the front of the skull, every tone a multiplier on
 * the instance's own skin colour. See `TUNING.pedBuild.face` for the sizes and for the
 * measurement that says a face is worth 56 triangles at these camera distances.
 *
 * `zF` is the skull's front plane. Flat features sit `proud` in front of it so they cannot
 * z-fight; features with real depth (brow, nose, ears) start AT it and stand out, because the
 * whole reason they are geometry and not a texture is that they self-shade under a low sun and
 * therefore keep working when the figure turns away from key.
 */
function _pedFace(mb, skullCY, skullW, skullD) {
  const F = T.pedBuild.face;
  const zF = skullD * 0.5;

  // ── brow ridge: one bar across both eyes, standing proud. It is the shadow this casts on
  //    the eye band, not the bar itself, that reads at distance.
  mb.box(0, skullCY + F.browY, zF - F.browD * 0.5 + 0.008, F.browW, F.browH, F.browD,
    g(F.browTone), { noBottom: true, topZ: 0.55 });

  // ── eye sockets and their highlights. Two dark rectangles are the single strongest cue on
  //    a 26 px head; the pale pip inboard of each is what turns a socket into an eye.
  for (const s of [1, -1]) {
    mb.plateZ(s * F.eyeX, skullCY + F.eyeY, zF + F.proud, F.eyeW, F.eyeH, g(F.eyeTone));
    mb.plateZ(s * (F.eyeX - F.eyeW * 0.16), skullCY + F.eyeY, zF + F.proud * 2,
      F.pupilW, F.pupilH, g(F.pupilTone));
  }

  // ── nose: the only feature that breaks the face's flat normal, so the only one that still
  //    reads when the head is lit edge-on. Tapered to the tip.
  mb.box(0, skullCY + F.noseY, zF + F.noseD * 0.5 - 0.006, F.noseW, F.noseH, F.noseD,
    g(F.noseTone), { noBottom: true, topX: 0.55, topZ: 0.45, frontZ: -F.noseD * 0.30 });

  // ── mouth.
  mb.plateZ(0, skullCY + F.mouthY, zF + F.proud, F.mouthW, F.mouthH, g(F.mouthTone));

  // ── ears: silhouette rather than shading, which makes them the feature that survives a
  //    profile view — and most of a crowd is seen in profile.
  for (const s of [1, -1]) {
    mb.box(s * (skullW * 0.5 + F.earW * 0.5 - F.earOut), skullCY + F.earY, -0.004,
      F.earW, F.earH, F.earD, g(F.earTone), { noBottom: true, topZ: 0.80 });
  }
}

/**
 * HANDS — their own mesh, one geometry per archetype, on the SKIN tint.
 *
 * Split out of the head mesh in round 12 for two reasons. First, hands have to follow the
 * archetype's arm pose (`pedArmGeometry`) and the head does not, so they cannot share a
 * geometry. Second, the round-11 hand was computed independently of the sleeve and ended up
 * 8.4 mm — under one pixel — clear of it; both are now derived from one function, so they
 * cannot disagree again.
 *
 * Costs one draw call per archetype and 20 triangles per pedestrian.
 */
function buildPedHands(a) {
  const B = T.pedBuild;
  const mb = new Mesher();
  const skin = g(1.0);
  for (const s of [1, -1]) {
    const L = pedArmGeometry(a, s);
    // Hung from the wrist, overlapping it by 6 mm so no camera angle can find a seam.
    mb.box(s * L.foreX, L.wristY + 0.006 - B.handH * 0.5, L.wristZ + 0.004,
      B.handW, B.handH, B.handD, skin, { noBottom: true, topX: 1.06, topZ: 1.06 });
  }
  return mb.toGeometry('traffic.ped.hands.' + a.id);
}

/* ═════════════════════════════════════════════════════════════════ shared ══ */

/** One wheel, shared by every vehicle kind. Scaled per instance. Unit radius 1, width 1. */
function buildWheel() {
  const mb = new Mesher();
  const W = T.visual.wheelTone;
  mb.wheelX(0, 0, 0, 1, 1, T.visual.wheelSegments, g(W.tread), g(W.rim));
  // A single lighter spoke bar so rotation is actually visible.
  mb.box(0, 0, 0, 1.02, 0.16, 1.6, g(W.spoke), { noBottom: true });
  return mb.toGeometry('traffic.wheel');
}

/**
 * THE PARKED CAR. Three geometries, because one instanced mesh cannot do this job.
 *
 * WHAT WAS WRONG (round-4 blind critic, `street-speed`)
 *   "The foreground right corner — the most-read corner after the hero — is occupied by a
 *   placeholder: an untextured white cuboid with a hard black L-shaped void where a car
 *   should be, and the parked cars behind it are slabs with no wheels."
 *
 *   Both halves of that came from packing the whole car onto ONE mesh whose diffuse is
 *   multiplied by the car's paint. Glazing at 0.085 × white paint is the black void; the
 *   wheels were flat plates 8 mm proud of the flank that read as door cards; and the
 *   contact shadow was a quad 0.22 m wider than the body, i.e. entirely hidden underneath
 *   the car from every camera angle we ever ship.
 *
 * THE SPLIT
 *   `body`       — paint material, tinted per instance. Lower box + glasshouse only.
 *   `detailNear` — TYRE material, instanceColor left at 1, so its vertex colours are exact
 *                  absolute albedos on every car: contact blob, four real wheels seated in
 *                  their arches, dark glazing, tail and head lenses, plates, rocker.
 *   `detailFar`  — the same mesh's cheap twin: contact blob, glazing band and flank plates.
 *
 * Exactly one of the two detail geometries is drawn per tile per frame (see parked.js), so
 * the field costs ~38 tri/car at distance and ~124 tri/car in the near field.
 *
 * The instance matrix carries (position, yaw, per-axis scale), so one geometry covers
 * hatchback → estate → small van without a second draw call.
 */
function buildParkedCar() {
  const K = T.parked;
  const m = K.body;
  const D = K.detail;
  const C = K.detailTone;

  const hw = m.W * 0.5, hl = m.L * 0.5;
  const lowH = m.beltline - m.floor;
  const cabH = m.roof - m.beltline;
  const cabHW = m.W * D.cabW * 0.5;
  const cabHL = m.L * D.cabL * 0.5;
  const cabC = m.L * D.cabZ;

  /* ── the painted shell ─────────────────────────────────────────────────────────── */
  const shell = new Mesher();
  const body = g(V.body);
  const roofC = g(V.roof);
  const pillar = g(V.body * D.pillarTone);
  const bump = g(V.bumper);
  const dark = g(V.dark);

  // Lower body. Front and back take the bumper tone; the flanks are pulled well below the
  // top so a light car does not read as one continuous slab of white.
  shell.box(0, m.floor + lowH * 0.5, 0, m.W, lowH, m.L, body, {
    top: body, side: g(V.body * 0.78), topX: 0.99, frontZ: -0.13, backZ: 0.07,
    front: bump, back: bump, bottom: dark, noBottom: true,
  });

  // Glasshouse. The FRAME is paint (roof on top, pillar tone on the flanks) and the glass
  // itself is laid over it by the detail mesh. Kept broad on purpose: from a chase camera
  // or an elevated establishing shot the roof is most of what you see, and a steeply raked
  // cabin turns the whole kerb into a line of dark wedges.
  shell.box(0, m.beltline + cabH * 0.5, cabC, cabHW * 2, cabH, cabHL * 2, pillar, {
    top: roofC, side: pillar, front: pillar, back: pillar,
    topX: D.cabTopX, topZ: D.cabTopZ, frontZ: D.cabFrontZ, backZ: D.cabBackZ,
    noBottom: true,
  });

  /* ── the untinted detail, near ─────────────────────────────────────────────────── */
  const near = new Mesher();
  _parkedBlob(near);
  _parkedGlass(near, cabHW, cabHL, cabC, m.beltline, cabH, D, C);

  // Rocker: closes the gap between the sill and the tarmac between the axles, so you cannot
  // see daylight under a parked car.
  for (let i = 0; i < 2; i++) {
    const s = i === 0 ? 1 : -1;
    near.plateX(s * (hw - 0.012), (D.rockerY0 + m.floor) * 0.5, 0,
      m.L * D.rockerZ * 2, m.floor - D.rockerY0, C.rocker, s);
  }

  // Four wheels, seated 6 mm INBOARD of the flank so the bodywork cuts the arch for free.
  const wr = D.wheelR;
  for (let i = 0; i < 2; i++) {
    const s = i === 0 ? 1 : -1;
    const wx = s * (hw - D.wheelInset);
    near.wheelParked(wx, wr, hl * D.axleF, wr, D.wheelW, D.wheelSegs, C.tyre, C.rim, s);
    near.wheelParked(wx, wr, hl * D.axleR, wr, D.wheelW, D.wheelSegs, C.tyre, C.rim, s);
  }

  // Lenses and plates. Small, but they are the difference between a box and a car at 8 m.
  const fz = hl + D.lampProud, bz = -hl - D.lampProud;
  for (let i = 0; i < 2; i++) {
    const s = i === 0 ? 1 : -1;
    near.plateZ(s * hw * D.tailX, m.floor + D.tailY, bz, D.tailW, D.tailH, C.tail);
    near.plateZ(s * hw * D.headX, m.floor + D.headY, fz, D.headW, D.headH, C.head);
  }
  near.plateZ(0, m.floor + D.plateY, fz, D.plateW, D.plateH, C.plate);
  near.plateZ(0, m.floor + D.plateY, bz, D.plateW, D.plateH, C.plate);

  /* ── the untinted detail, far ──────────────────────────────────────────────────── */
  const far = new Mesher();
  _parkedBlob(far);
  _parkedGlass(far, cabHW, cabHL, cabC, m.beltline, cabH, D, C);
  // At range a flat dark plate at the arch reads identically to a cylinder and costs 2 tri.
  const fwH = wr * D.farWheelH, fwL = wr * D.farWheelL;
  for (let i = 0; i < 2; i++) {
    const s = i === 0 ? 1 : -1;
    far.plateX(s * (hw + 0.006), fwH * 0.5, hl * D.axleF, fwL, fwH, C.tyre, s);
    far.plateX(s * (hw + 0.006), fwH * 0.5, hl * D.axleR, fwL, fwH, C.tyre, s);
  }

  return {
    body: shell.toGeometry('traffic.parked.body'),
    detailNear: near.toGeometry('traffic.parked.detail'),
    detailFar: far.toGeometry('traffic.parked.detailFar'),
  };
}

/** The hard contact blob, shared by both parked LODs. §3.4 — near-black, and WIDER than the car. */
function _parkedBlob(mb) {
  const m = T.parked.body;
  const S = T.visual;
  mb.contactBlob(0, S.shadowY, 0,
    m.W + S.shadowPadX * 2, m.L + S.shadowPadZ * 2, g(S.shadowValue));
}

/**
 * Dark glazing laid over the paint frame: a side light on each flank plus a windscreen and a
 * rear screen, all four RAKED to follow the glasshouse taper.
 *
 * Every one of them has to be a trapezoid, not a rectangle. The cabin tapers hard toward the
 * roof — that taper is what makes the thing read as a car from an elevated establishing shot
 * rather than as a second box stacked on the first — and a rectangular plate laid on a
 * tapering face pokes out past the pillars at the top and reads as a glass wing bolted to
 * the roof. Same class of mistake as the flat plates that used to stand in for wheels.
 */
function _parkedGlass(mb, cabHW, cabHL, cabC, beltline, cabH, D, C) {
  // Cabin cross-section at height fraction `t`: half-width, y, and the front/back z edges.
  const prof = (t) => {
    const k = 1 + (D.cabTopX - 1) * t;
    const kz = 1 + (D.cabTopZ - 1) * t;
    return {
      x: cabHW * k,
      y: beltline + cabH * t,
      zF: cabC + cabHL * kz + D.cabFrontZ * t,
      zB: cabC - cabHL * kz + D.cabBackZ * t,
    };
  };
  const p = D.glassProud;

  // ── side lights
  const a = prof(D.glassY0), b = prof(D.glassY1);
  const inA = (a.zF - a.zB) * 0.5 * (1 - D.glassL), inB = (b.zF - b.zB) * 0.5 * (1 - D.glassL);
  const aF = a.zF - inA, aB = a.zB + inA, bF = b.zF - inB, bB = b.zB + inB;
  mb.quad([a.x + p, a.y, aF], [a.x + p, a.y, aB], [b.x + p, b.y, bB], [b.x + p, b.y, bF], C.glass, 1);
  mb.quad([-a.x - p, a.y, aB], [-a.x - p, a.y, aF], [-b.x - p, b.y, bF], [-b.x - p, b.y, bB], C.glass, 1);

  // ── windscreen and rear screen
  const c = prof(D.screenT0), d = prof(D.screenT1);
  const cw = c.x * D.screenW, dw = d.x * D.screenW;
  mb.quad([-cw, c.y, c.zF + p], [cw, c.y, c.zF + p], [dw, d.y, d.zF + p], [-dw, d.y, d.zF + p], C.glass, 1);
  mb.quad([cw, c.y, c.zB - p], [-cw, c.y, c.zB - p], [-dw, d.y, d.zB - p], [dw, d.y, d.zB - p], C.glass, 1);
}

/**
 * The moving-vehicle contact shadow: a unit quad in the XZ plane, +Y up, centred.
 * Instanced on the tyre material so it adds one draw call and zero shader programs.
 * Built through the Mesher rather than PlaneGeometry because the tyre material runs with
 * `vertexColors = true` and a geometry without a colour attribute would render black.
 */
function buildBlob() {
  const mb = new Mesher();
  mb.contactBlob(0, 0, 0, 1, 1, g(T.visual.shadowValue));
  return mb.toGeometry('traffic.blobShadow');
}

/** A unit quad in the XY plane facing +Z. Lamp faces. */
function buildLamp() {
  const g2 = new THREE.PlaneGeometry(1, 1);
  g2.name = 'traffic.lamp';
  return g2;
}

/** A unit quad lying in the XZ plane, +Y up, centred at origin, extending +Z. Light pool. */
function buildPool() {
  const g2 = new THREE.PlaneGeometry(1, 1);
  g2.rotateX(-Math.PI / 2);
  g2.translate(0, 0, 0.5);
  g2.name = 'traffic.lightpool';
  return g2;
}

/**
 * The wet-road reflection streak: the same unit quad as the pool, kept separate so the two
 * can be disposed independently and so the streak can grow its own segmentation later without
 * disturbing the headlight cone. Origin at the lamp end, running +Z.
 */
function buildStreak() {
  const g2 = new THREE.PlaneGeometry(1, 1);
  g2.rotateX(-Math.PI / 2);
  g2.translate(0, 0, 0.5);
  g2.name = 'traffic.streak';
  return g2;
}

/**
 * Generated WET-ROAD STREAK texture — reference/REFERENCE.md §9.3.
 *
 * v = 0 at the lamp, 1 at the far end. Three things have to be true for this to read as a
 * reflection rather than as a glowing rectangle painted on the road:
 *
 *   1. It is BRIGHTEST AT THE SOURCE and falls off along its length, because the far end of a
 *      reflection is the part scattered by the most water.
 *   2. It stays NARROW the whole way. The headlight pool opens out into a wedge because it is
 *      a beam; a reflection is a smear of one point and it holds its width, which is what
 *      makes criterion 4's "at least 8× longer than wide" true of the drawn pixels and not
 *      just of the quad.
 *   3. It is BROKEN INTO SEGMENTS. §9.3: "tyre tracks in the wet surface break the reflection
 *      into segments." Without this it is a clean airbrushed lozenge and reads as a decal.
 *      The bands are a fixed function of v — no RNG — so shots stay bit-reproducible (§7.3).
 */
export function makeStreakTexture(size, breakup) {
  const cv = document.createElement('canvas');
  cv.width = size; cv.height = size;
  const c = cv.getContext('2d');
  const img = c.createImageData(size, size);
  const d = img.data;
  for (let j = 0; j < size; j++) {
    const v = j / (size - 1);
    // Bright core near the lamp, long tail. Two exponentials read better than one: the
    // first 15% is nearly solid, then it drops away over the remaining length.
    const along = Math.exp(-v * 3.1) * 0.82 + Math.exp(-v * 9.0) * 0.28;
    // Tyre-track bands running ACROSS the streak, tightening with distance the way ruts do
    // in perspective. Never cuts to zero — a reflection thins, it does not switch off.
    const bands = 1 - breakup * (0.5 + 0.5 * Math.sin(v * 21.0 + Math.sin(v * 5.0) * 2.2));
    // Slight waist: widest just past the source, tapering at the far end.
    const halfWidth = 0.30 + 0.13 * Math.sin(v * Math.PI * 0.9) - v * 0.11;
    for (let i = 0; i < size; i++) {
      const u = i / (size - 1) - 0.5;
      const t = Math.abs(u) / Math.max(0.02, halfWidth);
      // Soft-shouldered falloff across the width — hard edges read as geometry.
      const across = t >= 1 ? 0 : (1 - t * t) * (1 - t * t);
      const a = along * across * bands;
      const k = (j * size + i) * 4;
      d[k] = 255; d[k + 1] = 255; d[k + 2] = 255;
      d[k + 3] = Math.min(255, Math.max(0, (a * 255) | 0));
    }
  }
  c.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.needsUpdate = true;
  tex.name = 'traffic.streakGradient';
  return tex;
}

/**
 * Generated headlight-pool texture: a soft wedge, bright at the lamp, fading out and
 * narrowing to nothing at the far end. Canvas only — no external asset (N1).
 */
export function makePoolTexture(size) {
  const cv = document.createElement('canvas');
  cv.width = size; cv.height = size;
  const c = cv.getContext('2d');
  const img = c.createImageData(size, size);
  const d = img.data;
  for (let j = 0; j < size; j++) {
    // v = 0 at the lamp, 1 at the far end
    const v = j / (size - 1);
    const along = Math.pow(1 - v, 1.5);
    const halfWidth = 0.16 + v * 0.42;      // the wedge opens out
    for (let i = 0; i < size; i++) {
      const u = i / (size - 1) - 0.5;
      const across = Math.max(0, 1 - Math.abs(u) / halfWidth);
      const a = along * across * across;
      const k = (j * size + i) * 4;
      d[k] = 255; d[k + 1] = 246; d[k + 2] = 224;
      d[k + 3] = Math.min(255, (a * 255) | 0);
    }
  }
  c.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.needsUpdate = true;
  tex.name = 'traffic.poolGradient';
  return tex;
}

/* ═══════════════════════════════════════════════════════════════════ export ══ */

/** Build every geometry the traffic system needs. Call once per init()/reset(). */
export function buildModels() {
  const kinds = {};
  for (const kind of Object.keys(T.models)) {
    kinds[kind] = buildVehicle(kind, T.models[kind]);
  }
  return {
    kinds,
    kindNames: Object.keys(kinds),
    cyclist: {
      rider: buildCyclistRider(),
      bike: buildCyclistBike(),
      rig: { halfW: 0.30, halfL: T.bike.base * 0.5 + 0.22, height: 1.7, tailY: 0.72, tailZ: -T.bike.base * 0.5 - 0.06 },
    },
    // One geometry per archetype (§ pedArchetypes) — see the header note on why a tint
    // cannot substitute for an outline.
    pedBodies: T.pedArchetypes.map((a) => buildPedBody(a)),
    // Hands are per-ARCHETYPE (they follow that archetype's arm pose) while legs are per-POSE
    // and chosen per instance. See TUNING.pedArmPoses / pedLegPoses.
    pedHands: T.pedArchetypes.map((a) => buildPedHands(a)),
    pedLegs: T.pedLegPoses.map((pose) => buildPedLegs(pose)),
    pedHead: buildPedHead(),
    wheel: buildWheel(),
    lamp: buildLamp(),
    pool: buildPool(),
    streak: buildStreak(),
    blob: buildBlob(),
    parked: buildParkedCar(),
  };
}

export function disposeModels(models) {
  if (!models) return;
  for (const k of models.kindNames) models.kinds[k].geo.dispose();
  models.cyclist.rider.dispose();
  models.cyclist.bike.dispose();
  for (const geo of models.pedBodies) geo.dispose();
  for (const geo of models.pedHands) geo.dispose();
  for (const geo of models.pedLegs) geo.dispose();
  models.pedHead.dispose();
  models.wheel.dispose();
  models.lamp.dispose();
  models.pool.dispose();
  models.streak.dispose();
  models.blob.dispose();
  models.parked.body.dispose();
  models.parked.detailNear.dispose();
  models.parked.detailFar.dispose();
}
